import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { X } from "lucide-react";
import api from "@/lib/api";
import { collectAttribution, trackEvent } from "@/lib/settings";

export default function EnquiryModal({ open, onClose, defaultProperty = "", prefill = null }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    event_type: "Wedding",
    preferred_date: "",
    guest_count: "",
    property_interested: defaultProperty,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, property_interested: defaultProperty || f.property_interested, ...(prefill || {}) }));
  }, [open, defaultProperty, prefill]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, ...collectAttribution() };
      await api.post("/public/leads", payload);
      trackEvent("enquiry_submit", { event_type: form.event_type, property: form.property_interested });
      setDone(true);
      toast.success("Enquiry received. We'll be in touch within 24 hours.");
    } catch (e) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setDone(false); } }}>
      <DialogContent className="max-w-2xl bg-ivory border-ink/10 p-0 overflow-hidden" data-testid="enquiry-modal">
        <div className="grid grid-cols-1 md:grid-cols-5">
          <div className="hidden md:block md:col-span-2 bg-ink text-ivory p-8 relative">
            <div className="overline text-ivory/60">Plan a visit</div>
            <div className="display-2 mt-6 text-ivory">Tell us a little about your celebration.</div>
            <div className="absolute bottom-8 left-8 right-8 text-xs text-ivory/60 leading-relaxed">
              We respond within 24 hours with a tailored property recommendation, suite holds and a sample menu.
            </div>
          </div>
          <div className="md:col-span-3 p-6 md:p-10">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-light">
                {done ? "Thank you." : "Let's plan it"}
              </DialogTitle>
            </DialogHeader>

            {done ? (
              <div className="mt-6 space-y-4 text-sm text-graphite leading-relaxed">
                <p>Your enquiry is with our team. Expect a personal note from a planner within the next business day.</p>
                <button onClick={onClose} className="btn-ghost mt-4" data-testid="enquiry-close">Close</button>
              </div>
            ) : (
              <form className="mt-6 space-y-4" onSubmit={submit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input value={form.name} onChange={onChange("name")} placeholder="Your name *" data-testid="enquiry-name" required />
                  <Input value={form.phone} onChange={onChange("phone")} placeholder="Phone *" data-testid="enquiry-phone" required />
                </div>
                <Input value={form.email} onChange={onChange("email")} placeholder="Email" type="email" data-testid="enquiry-email" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select value={form.event_type} onValueChange={onChange("event_type")}>
                    <SelectTrigger data-testid="enquiry-event-type"><SelectValue placeholder="Event type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Wedding">Wedding</SelectItem>
                      <SelectItem value="Corporate Retreat">Corporate Retreat</SelectItem>
                      <SelectItem value="Private Celebration">Private Celebration</SelectItem>
                      <SelectItem value="Leisure Getaway">Leisure Getaway</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={form.preferred_date} onChange={onChange("preferred_date")} placeholder="Preferred date" type="date" data-testid="enquiry-date" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input value={form.guest_count} onChange={onChange("guest_count")} placeholder="Guest count" data-testid="enquiry-guests" />
                  <Select value={form.property_interested} onValueChange={onChange("property_interested")}>
                    <SelectTrigger data-testid="enquiry-property"><SelectValue placeholder="Property of interest" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Help me choose</SelectItem>
                      <SelectItem value="kai">Kai</SelectItem>
                      <SelectItem value="white-mist">White Mist</SelectItem>
                      <SelectItem value="yellow-moon">Yellow Moon</SelectItem>
                      <SelectItem value="aviana">Aviana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea value={form.message} onChange={onChange("message")} placeholder="Anything we should know?" rows={3} data-testid="enquiry-message" />
                <div className="flex items-center justify-between pt-2">
                  <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-graphite hover:text-ink" data-testid="enquiry-cancel">
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="enquiry-submit">
                    {submitting ? "Sending…" : "Send enquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
